/**
 * BuyNowButton，直接结算，点击元素本身触发
 * 
 * Attributes:
 * @param {String} data-variant-id 产品变体 ID，多个变体用英文逗号分隔
 * @param {String} data-quantity 购买数量，可选，默认为1，多个变体的购买数量用英文逗号分隔
 * 
 * @example 
 * ```html
 * <script src="{{ 'component-buy-button.js' | asset_url }}" defer="defer"></script>
 * <buy-now-button data-variant-id="{{ product.first_available_variant.id }}" data-quantity="1" class="button round">
 *   Buy Now
 *   {%- render 'loading-spinner' -%}
 * </buy-now-button>
 * ```
 */
if (!customElements.get('buy-now-button')) {
  class BuyNowButton extends HTMLElement {
    _shopify_features_script = document.querySelector("script[id='shopify-features']");
    _shopify_features_json = JSON.parse(this._shopify_features_script.innerHTML);
    _checkout_headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'authorization': 'Basic ' + btoa(this._shopify_features_json.accessToken)
    };

    get _variantId() { return this.getAttribute("data-variant-id") }
    get _quantity() { return this.getAttribute("data-quantity") ?? 1 }

    constructor() {
      super();
      this.loadingSpinner = this.querySelector('.loading__spinner ');
    }

    connectedCallback() {
      this.addEventListener('click', this.checkout.bind(this));
    }

    /**
     * 直接结算
     */
    checkout() {
      const variantIds = this._variantId.split(',');
      const quantities = this._quantity.split(",");

      const defaultQuantity = quantities[0] || 1; // 默认数量为1，如果 quantities 为空
      if (variantIds.length !== quantities.length) {
        console.warn("变体ID与数量的数量不匹配，将使用相同的数量值。");
        quantities.length = variantIds.length; // 将数量数组的长度调整为与变体ID数组相同
        quantities.fill(defaultQuantity); // 用默认数量填充整个数组
      }

      const giftItems = this.hasAttribute("data-include-gifts")
        ? this.collectGiftItems(parseInt(quantities[0], 10) || 1)
        : [];
      this.startLoading();
      this.checkoutByJson(variantIds, quantities, giftItems)
        .catch((err) => {
          console.error(err);
          this.checkoutByCartUrl(variantIds, quantities, giftItems);
        })
        .finally(() => {
          this.finishLoading();
          // this.codeBuryPoints();
        });
    }

    // 埋点
    codeBuryPoints() {
      const orderPrice = document.querySelector('.product-bottom__price').dataset.price;
      _lt('send', 'cv', {
        type: 'Purchase',
        value: orderPrice/100,
        currency: 'JPY'
      },['2c885a71-2bbc-4cbc-bf8a-5b3de8578fa2']);
    }

    collectGiftItems(mainQuantity) {
      const gifts = [];
      const seen = new Set();
      document.querySelectorAll("[data-cart-gift]").forEach((element) => {
        const variantId = Number(element.dataset.giftVariantId);
        const available = element.dataset.giftAvailable !== "false";
        if (!Number.isInteger(variantId) || variantId <= 0 || !available) return;
        if (seen.has(variantId)) return;
        seen.add(variantId);

        const giftText = element.querySelector(".product-gift__text")?.textContent;
        gifts.push({
          variantId,
          quantity: this.parseGiftQuantity(giftText, mainQuantity),
        });
      });
      return gifts;
    }

    parseGiftQuantity(value, fallbackQuantity) {
      const normalizedValue = String(value || "").replace(
        /[０-９]/g,
        (digit) => String.fromCharCode(digit.charCodeAt(0) - 0xfee0)
      );
      const match = normalizedValue.match(/数量\s*[:：]\s*(\d+)\s*点/);
      if (!match) return Math.max(1, Number(fallbackQuantity) || 1);
      return Math.max(1, Math.min(99, Number(match[1]) || 1));
    }

    checkoutByCartUrl(variantIds, quantities, giftItems = []) {
      let parmaString = '';
      for (let i = 0; i < variantIds.length; i++) {
        const variantId = variantIds[i];
        const quantity = quantities[i];
        if (i == 0) {
          parmaString = `${parmaString}${variantId}:${quantity}`;
        } else {
          parmaString = `${parmaString},${variantId}:${quantity}`;
        }
      }
      giftItems.forEach((gift) => {
        parmaString = parmaString
          ? `${parmaString},${gift.variantId}:${gift.quantity}`
          : `${gift.variantId}:${gift.quantity}`;
      });
      const cartRoute = window.routes?.cart_url ?? '/cart';;
      const checkoutUrl = `${cartRoute}/${parmaString}`;
      window.location.href = checkoutUrl;
    }

    async checkoutByJson(variantIds, quantities, giftItems = []) {
      let lineItems = variantIds.map((id, index) => {
        return {
          "variant_id": parseInt(id, 10),
          "quantity": parseInt(quantities[index], 10),
        }
      });
      giftItems.forEach((gift) => {
        lineItems.push({
          variant_id: gift.variantId,
          quantity: gift.quantity,
          properties: {
            _dreame_auto_gift: "true",
          },
        });
      });

      let checkout_json = {
        "checkout": {
          "line_items": lineItems,
          "secret": true,
          "wallet_name": "Checkout",
          "is_upstream_button": false,
          "page_type": "product",
          "has_selling_plans": false,
          "presentment_currency": window.shop_currency,
          "country": window.shop_locale_upcase,
        }
      }
      let data = JSON.stringify(checkout_json);
      return fetch("/wallets/checkouts.json",
        {
          method: 'POST',
          headers: this._checkout_headers,
          body: data,
        }
      ).then(function (response) {
        if (response.ok) {
          return response.text();
        }
      }).then(function (strj) {
        let checkout_json = JSON.parse(strj);
        location.href = checkout_json.checkout.web_url;
      });
    }

    startLoading() {
      this.setAttribute("aria-disabled", true);
      this.classList.add("loading");
      this.loadingSpinner?.classList.remove("hidden");
    }

    finishLoading() {
      this.removeAttribute("aria-disabled");
      this.classList.remove("loading");
      this.loadingSpinner?.classList.add("hidden");
    }
  }

  customElements.define('buy-now-button', BuyNowButton);
}

